/**
 * HereditaryRiskChecklist — iter624 — DKG checklist + NCCN 2026 recommendations.
 * Mounted at /dashboard/hereditary-risk (BHI prerequisite).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, ShieldAlert, FileCheck } from "lucide-react";
import { toast } from "sonner";

const TIER_COLOR_MAP = {
  emerald: "bg-emerald-500 text-white",
  amber:   "bg-amber-500 text-white",
  orange:  "bg-orange-500 text-white",
  rose:    "bg-rose-500 text-white",
};

export default function HereditaryRiskChecklist() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState([]);
  const [answers, setAnswers] = useState({});
  const [maternalCount, setMaternalCount] = useState(0);
  const [paternalCount, setPaternalCount] = useState(0);
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("female");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/bhi/hereditary-checklist`)
      .then(({ data }) => {
        if (data.has_data) {
          setResult(data);
          // Pre-fill form
          const a = data.answers || {};
          setAnswers(a);
          setMaternalCount(a.maternal_line_additional_count || 0);
          setPaternalCount(a.paternal_line_additional_count || 0);
          if (a.age) setAge(String(a.age));
          if (a.sex) setSex(a.sex);
          setCatalog(Object.entries(/* fallback catalog inferred from criteria */ {}).map(([k, v]) => v));
        }
        if (!data.has_data) setCatalog(data.criteria_catalog || []);
      })
      .catch(() => toast.error("Грешка при зареждане."))
      .finally(() => setLoading(false));
  }, [API]);

  const toggle = (key) => setAnswers((s) => ({ ...s, [key]: !s[key] }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...Object.fromEntries(
          Object.entries(answers).filter(([k]) => !["age", "sex", "maternal_line_additional_count", "paternal_line_additional_count"].includes(k))
        ),
        maternal_line_additional_count: parseInt(maternalCount) || 0,
        paternal_line_additional_count: parseInt(paternalCount) || 0,
        age: age ? parseInt(age) : null,
        sex,
      };
      const { data } = await axios.post(`${API}/bhi/hereditary-checklist`, payload);
      setResult(data);
      toast.success(`Резултат: ${data.tier_label_bg} (${data.score} точки)`);
      // iter624 — Live Insight Toast pattern: notify other twins
      window.dispatchEvent(new Event("hereditary-risk-changed"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Грешка при изчислението.");
    } finally {
      setSubmitting(false);
    }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="hereditary-page">
      <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
    </div>;
  }

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="hereditary-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert className="w-5 h-5 text-purple-500" />
          <h1 className="text-xl font-bold">Семейна анамнеза за рак на гърдата и яйчниците</h1>
        </div>
        <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Дигитализиран checklist на Deutsche Krebsgesellschaft + актуалните препоръки на NCCN 2026.
          Резултатът ще коригира BHI оценката ти.
        </p>

        {/* RESULT block (shown after submit or if pre-computed) */}
        {result && (
          <div className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`} data-testid="hereditary-result">
            <div className="flex items-baseline gap-3 mb-3">
              <div className="text-4xl font-bold tabular-nums" data-testid="hereditary-score">{result.score}</div>
              <div className="text-sm opacity-60">точки</div>
              <span className={`ml-auto px-3 py-1 rounded-full text-xs font-bold ${TIER_COLOR_MAP[result.tier_color] || "bg-slate-500"}`} data-testid="hereditary-tier">
                {result.tier_label_bg}
              </span>
            </div>
            {result.referral_indicated && (
              <div className={`p-3 rounded-lg mb-3 ${isDark ? "bg-orange-900/30 border border-orange-700/40" : "bg-orange-50 border border-orange-200"}`} data-testid="hereditary-referral">
                <strong className="text-sm">⚠ Препоръка за генетична консултация</strong>
                <p className="text-xs mt-1 opacity-90">
                  Резултатът надхвърля прага от 100 точки. Препоръчва се насочване към медицински генетик за BRCA1/BRCA2 тест.
                </p>
              </div>
            )}
            <div className="space-y-2" data-testid="hereditary-recs">
              {(result.recommendations || []).map((r, i) => (
                <div key={i} className={`p-3 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`} data-testid={`hereditary-rec-${i}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <strong className="text-xs uppercase tracking-wider">{r.category_bg}</strong>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      r.priority === "very_high" ? "bg-rose-200 text-rose-900" :
                      r.priority === "high" ? "bg-orange-200 text-orange-900" :
                      r.priority === "enhanced" ? "bg-amber-200 text-amber-900" :
                      "bg-emerald-200 text-emerald-900"
                    }`}>{r.priority}</span>
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{r.action_bg}</p>
                  <p className={`text-[10px] mt-1 italic opacity-60`}>{r.evidence_bg}</p>
                </div>
              ))}
            </div>
            {result.disclaimer_bg && (
              <p className={`text-[11px] mt-3 italic opacity-70`} data-testid="hereditary-disclaimer">
                ⚠ {result.disclaimer_bg}
              </p>
            )}
          </div>
        )}

        {/* FORM */}
        <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="hereditary-form">
          <h2 className="font-bold text-sm mb-2">Попълни приложимите критерии</h2>
          <p className="text-[11px] opacity-70 mb-3">
            Маркирай само наличните в семейната ти анамнеза. Точките са според DKG скалата.
          </p>

          {/* Demographics */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] opacity-70">Възраст</span>
              <input type="number" min="14" max="110" value={age} onChange={(e) => setAge(e.target.value)} data-testid="age-input"
                className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] opacity-70">Пол</span>
              <select value={sex} onChange={(e) => setSex(e.target.value)} data-testid="sex-input"
                className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`}>
                <option value="female">жена</option>
                <option value="male">мъж</option>
                <option value="other">друго</option>
              </select>
            </label>
          </div>

          {/* Criteria checkboxes */}
          <div className="space-y-2 mb-4">
            {catalog.filter(c => !["maternal_line_additional_count", "paternal_line_additional_count"].includes(c.key)).map((c) => (
              <label key={c.key} data-testid={`criterion-${c.key}`}
                     className={`flex items-start gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                       answers[c.key] ? (isDark ? "bg-purple-900/30 border border-purple-500/40" : "bg-purple-50 border border-purple-300") : ""
                     }`}>
                <input type="checkbox" checked={!!answers[c.key]} onChange={() => toggle(c.key)}
                       className="mt-1 accent-purple-500" />
                <span className="flex-1 text-sm">{c.label_bg}</span>
                <span className={`text-xs font-bold tabular-nums ${isDark ? "text-purple-300" : "text-purple-700"}`}>
                  +{c.score} т.
                </span>
              </label>
            ))}
          </div>

          {/* Line aggregates */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] opacity-70">Допълнителни случаи в майчината линия (×8 т.)</span>
              <input type="number" min="0" max="20" value={maternalCount} onChange={(e) => setMaternalCount(e.target.value)} data-testid="maternal-count"
                className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] opacity-70">Допълнителни случаи в бащината линия (×6 т.)</span>
              <input type="number" min="0" max="20" value={paternalCount} onChange={(e) => setPaternalCount(e.target.value)} data-testid="paternal-count"
                className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
            </label>
          </div>

          <Button onClick={submit} disabled={submitting}
                  className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                  data-testid="submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileCheck className="w-4 h-4 mr-2" />}
            Изчисли наследствен риск
          </Button>
        </div>
      </div>
    </div>
  );
}
